import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import { Box, Tabs, Tab } from '@mui/material';

/**
 * 마크다운 에디터 컴포넌트
 * Monaco Editor와 미리보기를 제공하는 에디터
 * 
 * @param {Object} props
 * @param {string} props.value - 에디터 값
 * @param {Function} props.onChange - 값 변경 핸들러
 * @param {string} props.label - 라벨
 * @param {boolean} props.error - 에러 상태
 * @param {string} props.helperText - 도움말 텍스트
 * @param {number} props.height - 에디터 높이 (기본값: 400px)
 */
const MarkdownEditor = ({
    value = '',
    onChange,
    label,
    error = false,
    helperText,
    height = 400,
}) => {
    const [viewMode, setViewMode] = useState('split'); // 'editor', 'preview', 'split'

    /**
     * 뷰 모드 변경 핸들러
     */
    const handleViewModeChange = (event, newValue) => {
        setViewMode(newValue);
    };

    /**
     * 에디터 값 변경 핸들러
     */
    const handleEditorChange = (newValue) => {
        onChange?.(newValue || '');
    };

    return (
        <EditorContainer>
            {label && (
                <LabelContainer>
                    <Label error={error}>{label}</Label>
                </LabelContainer>
            )}
            
            <TabsContainer>
                <Tabs
                    value={viewMode}
                    onChange={handleViewModeChange}
                    sx={{
                        minHeight: '36px',
                        '& .MuiTab-root': {
                            minHeight: '36px',
                            fontSize: '0.875rem',
                            padding: '6px 16px',
                        },
                    }}
                >
                    <Tab label="에디터" value="editor" />
                    <Tab label="미리보기" value="preview" />
                    <Tab label="분할" value="split" />
                </Tabs>
            </TabsContainer>

            <EditorWrapper>
                {(viewMode === 'editor' || viewMode === 'split') && (
                    <EditorSection split={viewMode === 'split'}>
                        <Editor
                            height={`${height}px`}
                            language="markdown"
                            theme="vs-light"
                            value={value || ''}
                            onChange={handleEditorChange}
                            options={{
                                minimap: { enabled: false },
                                fontSize: 14,
                                scrollBeyondLastLine: false,
                                wordWrap: 'on',
                                lineNumbers: 'on',
                                automaticLayout: true,
                            }}
                        />
                    </EditorSection>
                )}

                {(viewMode === 'preview' || viewMode === 'split') && (
                    <PreviewSection split={viewMode === 'split'}>
                        <MarkdownViewer content={value} />
                    </PreviewSection>
                )}
            </EditorWrapper>

            {helperText && (
                <HelperText error={error}>{helperText}</HelperText>
            )}
        </EditorContainer>
    );
};

/**
 * 마크다운 뷰어 컴포넌트
 * Typora drake-light 테마 스타일 적용
 */
const MarkdownViewer = ({ content, className }) => {
    if (!content) {
        return (
            <EmptyPreview>
                <EmptyText>미리보기 내용이 없습니다.</EmptyText>
            </EmptyPreview>
        );
    }

    return (
        <MarkdownContainer className={className}>
            <ReactMarkdown>{content}</ReactMarkdown>
        </MarkdownContainer>
    );
};

// 스타일 컴포넌트
const EditorContainer = styled(Box)`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const LabelContainer = styled(Box)`
    margin-bottom: 8px;
`;

const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${props => props.error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)'};
    display: block;
`;

const TabsContainer = styled(Box)`
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 8px;
`;

const EditorWrapper = styled(Box)`
    display: flex;
    width: 100%;
    border: 1px solid ${props => props.error ? '#d32f2f' : '#e0e0e0'};
    border-radius: 4px;
    overflow: hidden;
`;

const EditorSection = styled(Box)`
    flex: ${props => props.split ? '1' : '1'};
    min-width: 0;
    border-right: ${props => props.split ? '1px solid #e0e0e0' : 'none'};
`;

const PreviewSection = styled(Box)`
    flex: ${props => props.split ? '1' : '1'};
    min-width: 0;
    overflow-y: auto;
    background-color: #ffffff;
    padding: 16px;
    max-height: ${props => props.split ? 'none' : '400px'};
`;

const HelperText = styled.div`
    font-size: 0.75rem;
    margin-top: 4px;
    color: ${props => props.error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)'};
    padding-left: 14px;
`;

const EmptyPreview = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 200px;
`;

const EmptyText = styled.div`
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.875rem;
`;

// Typora drake-light 테마 기반 스타일 (폰트 50% 축소)
const MarkdownContainer = styled.div`
  width: 100%;
  line-height: 1.6;
  color: #333333;
  font-size: 1rem;
  font-family: 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: subpixel-antialiased;
  text-rendering: optimizeLegibility;
  letter-spacing: 0;

  * {
    font-size: 0.7rem;
  }
  /* 헤딩 스타일 */
  h1, h2, h3, h4, h5, h6 {
    font-family: inherit;
    position: relative;
    margin-top: 1.3rem;
    margin-bottom: 0.6rem;
    font-weight: bold;
    line-height: 1.4;
    cursor: text;
    color: #273849;
  }

  h1 {
    font-size: 0.775rem;
    text-align: center;
    margin-top: 0;
  }

  h2 {
    font-size: 0.75rem;
    display: inline-block;
  }

  h2:after {
    display: block;
    content: '';
    height: 2px;
    margin-top: 4px;
    background-color: #273849;
    border-radius: 2px;
  }

  h3 {
    font-size: 0.725rem;
  }

  h4 {
    font-size: 0.7rem;
  }

  h5 {
    font-size: 0.675rem;
  }

  h6 {
    font-size: 0.65rem;
  }

  h1 code, h2 code, h3 code, h4 code, h5 code, h6 code {
    font-size: inherit !important;
  }

  /* 단락 스타일 */
  p, blockquote, ul, ol, dl, table {
    margin: 0.5em 0;
  }

  p {
    line-height: 1.6;
    color: #333333;
  }

  /* 리스트 스타일 */
  ul, ol {
    padding-inline-start: 2em;
    line-height: 1.6;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  ol > li {
    list-style-type: decimal-leading-zero;
  }

  li {
    margin: 0.2em 0;
    color: #333333;
    font-size: 0.65rem;
  }

  li > ol,
  li > ul {
    margin: 0;
  }

  li p.first {
    display: inline-block;
  }

  ul:first-child,
  ol:first-child {
    margin-top: 0;
  }

  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
  }

  /* 강조 스타일 */
  strong, b {
    color: #273849;
    font-weight: bold;
  }

  em, i {
    padding: 0 5px 0 2px;
    font-style: italic;
  }

  /* 코드 스타일 */
  code, tt {
    margin: 0 2px;
    color: #d63200;
    font-family: 'JetBrains Mono', 'Fira Code', Menlo, 'Ubuntu Mono', Consolas, monospace;
  }

  code {
    padding: 2px 6px;
    background-color: #f6f8fa;
    border-radius: 2px;
    font-size: 0.65rem;
  }

  pre {
    background-color: #f6f8fa;
    color: #24292e;
    padding: 0.5rem !important;
    border-radius: 2px;
    overflow-x: auto;
    margin: 0.5em 0;
    border: none;
    word-wrap: normal;
    font-size: 0.65rem;
  }

  pre code {
    background-color: transparent;
    padding: 0;
    margin: 0;
    color: #24292e;
    font-size: 0.65rem;
  }

  /* 링크 스타일 */
  a {
    color: #d63200;
    text-decoration: none;
    margin: 0 0.2rem;
  }

  a:hover {
    border-bottom: 0.1rem solid #d63200;
  }

  a:any-link {
    color: #d63200;
  }

  /* 인용구 스타일 */
  blockquote {
    color: #333333;
    border-radius: 2px;
    padding: 8px 14px;
    background-color: #fdefee;
    position: relative;
    width: fit-content;
    border-left: none;
    margin: 0.5em 0;
  }

  blockquote:before {
    display: block;
    position: absolute;
    content: '';
    width: 4px;
    left: 0;
    top: 0;
    height: 100%;
    background-color: #e95f59;
    border-radius: 2px;
  }

  blockquote strong {
    color: #333333;
  }

  /* 테이블 스타일 */
  table {
    padding: 0;
    word-break: initial;
    background-color: #ffffff;
    margin: 0.5em 0;
    width: 100%;
    border-collapse: collapse;
  }

  table tr {
    border-top: 0.1em solid #dfe2e5;
    margin: 0;
    padding: 0;
  }

  table th {
    font-weight: bold;
    border: 0.1em solid #dfe2e5;
    border-bottom: 0;
    margin: 0;
    padding: 6px 13px;
    background-color: #f8f8f8;
  }

  table td {
    border: 0.1em solid #dfe2e5;
    margin: 0;
    padding: 6px 13px;
  }

  table thead {
    background-color: #f8f8f8;
  }

  table thead th {
    background-color: #f8f8f8;
  }

  table tr:nth-child(2n) {
    background-color: #f8f8f8;
  }

  table tr th:first-child,
  table tr td:first-child {
    margin-top: 0;
  }

  table tr th:last-child,
  table tr td:last-child {
    margin-bottom: 0;
  }

  /* 수평선 스타일 */
  hr {
    height: 2px;
    padding: 0;
    margin: 10px 0;
    background-color: #273849;
    border: 0 none;
    overflow: hidden;
    box-sizing: content-box;
  }

  /* 이미지 스타일 */
  img {
    border-left: none;
    border-right: none;
    vertical-align: baseline;
    border-radius: 2px;
    max-width: 100%;
    height: auto;
    margin: 0.5em 0;
  }

  /* 삭제선 스타일 */
  del {
    padding: 1px 2px;
  }

  /* 체크박스 리스트 스타일 */
  input[type="checkbox"] {
    margin-right: 0.5em;
    margin-left: -1.3em;
  }

  /* 첫 번째 요소 마진 제거 */
  > *:first-child {
    margin-top: 0 !important;
  }

  > *:last-child {
    margin-bottom: 0 !important;
  }

  > h2:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  > h1:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  > h1:first-child + h2 {
    margin-top: 0;
    padding-top: 0;
  }

  > h3:first-child,
  > h4:first-child,
  > h5:first-child,
  > h6:first-child {
    margin-top: 0;
    padding-top: 0;
  }
`;

export default MarkdownEditor;
